import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  buttonColor,
  buttonHoverColor,
  primaryColor,
} from "../../UtilColorPalate/UtilColorPalate";
import { SubmitFormAPI } from "../../Utility/UtilApi/UtilApi";
import UtilToastMessages from "../../Utility/UtilToastMessages/UtilToastMessages";

const ContactUs = () => {
  const [isSnackbarOpen, setSnackbarOpen] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);

  // Initial state for form data
  const initialContactData = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  };

  // State to store form data
  const [contactData, setContactData] = useState(initialContactData);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Validate phone number to only accept numbers
    if (name === "phone" && !/^\d*$/.test(value)) {
      return; // Do not update state if the input is not a number
    }

    // Validate email in real-time
    if (name === "email") {
      const isValid = /\S+@\S+\.\S+/.test(value); // Basic email validation
      setIsEmailValid(isValid);
    }

    // Update state
    setContactData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };


    useEffect(() => {
      window.scrollTo(0, 0); // Scroll to top on mount
    }, []);
  
  // Validate fields before submission
  const validateFields = () => {
    const { firstName, lastName, email, phone } = contactData;

    if (!firstName || !lastName || !email || !phone) {
      alert("Please fill out all required fields (First Name, Last Name, Email, Phone).");
      return false;
    }

    if (!isEmailValid) {
      alert("Please enter a valid email address.");
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (validateFields()) {
      try {
        const payload = {
          tablename: "leads",
          data: {
            firstname: contactData.firstName,
            lastname: contactData.lastName,
            email: contactData.email,
            phone: contactData.phone,
            // "description": contactData.message, // Uncomment if needed
            origin: "Website",
            status: "In Progress",
          },
        };

        const response = await SubmitFormAPI(payload);
        if (response.statusCode === 201) {
          setSnackbarOpen(true);
          // Reset form fields after successful submission
          setContactData(initialContactData);
          setIsEmailValid(true); // Reset email validation state
        }
      } catch (e) {
        console.error("An error occurred:", e);
      }

      console.log("Form Data Submitted:", contactData);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: { xs: 1, md: 8 },
        }}
      >
        {/* Heading */}
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          Get In Touch
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          textAlign="center"
          maxWidth="600px"
          mb={4}
          sx={{ color: primaryColor }}
        >
          Please fill out the form and we will get back to you.
        </Typography>

        {/* Form and Map */}
        <Grid container spacing={4} maxWidth="900px">
          <Grid item xs={12} md={6} style={{ alignContent: "center" }}>
            {/* First Name Field */}
            <TextField
              fullWidth
              label="First Name"
              variant="outlined"
              margin="dense"
              name="firstName"
              value={contactData.firstName}
              onChange={handleInputChange}
              sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
            />
            {/* Last Name Field */}
            <TextField
              fullWidth
              label="Last Name"
              variant="outlined"
              margin="dense"
              name="lastName"
              value={contactData.lastName}
              onChange={handleInputChange}
              sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
            />
            <TextField
              fullWidth
              label="Your E-mail"
              variant="outlined"
              margin="dense"
              name="email"
              value={contactData.email}
              onChange={handleInputChange}
              error={!isEmailValid}
              helperText={!isEmailValid ? "Please enter a valid email address." : ""}
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: isEmailValid ? "inherit" : "red",
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Phone number"
              variant="outlined"
              margin="dense"
              name="phone"
              value={contactData.phone}
              onChange={handleInputChange}
              sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
            />
            <TextField
              fullWidth
              label="Your Message Here"
              variant="outlined"
              margin="dense"
              name="message"
              value={contactData.message}
              onChange={handleInputChange}
              multiline
              rows={4}
              sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
             sx={{
                                                                    backgroundColor: buttonColor,
                                                                    color: "#fff",
                                                                    textTransform: "none",
                                                                    padding: "5px 15px",
                                                                    borderRadius: "4px",
                                                                    "&:hover": { backgroundColor: buttonHoverColor },
                                                                  }}
            >
              Submit
            </Button>
          </Grid>

          {/* Google Maps Placeholder */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 1,
                borderRadius: 3,
                textAlign: "center",
                fontSize: "14px",
                color: "gray",
              }}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3590.432444302494!2d28.179405699999997!3d-25.855240899999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e9564462d71075d%3A0x88aa399603681c10!2s5%20Bosbok%20Nook%2C%20Zwartkop%2C%20Centurion%2C%200157%2C%20South%20Africa!5e0!3m2!1sen!2sin!4v1739280833477!5m2!1sen!2sin"
                width="600"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </Paper>
          </Grid>
        </Grid>

        {/* Contact Info */}
        <Grid
          container
          spacing={3}
          justifyContent="space-around"
          alignItems="center"
          mt={10}
        >
          <Grid item xs={12} sm={4} display="flex" alignItems="center">
            <IconButton sx={{ color: primaryColor }}>
              <LocationOnIcon sx={{ margin: { xs: "15px", md: "17px" }, fontSize: { xs: "30px", md: "40px" } }} />
            </IconButton>
            <Typography variant="body1" sx={{ fontSize: "15px" }}>
              <strong>Address:</strong> 5 Bosbok Nook Street, Centurion
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4} display="flex" alignItems="center">
            <IconButton sx={{ color: primaryColor }}>
              <PhoneIcon sx={{ margin: { xs: "15px", md: "17px" }, fontSize: { xs: "30px", md: "40px" } }} />
            </IconButton>
            <Typography variant="body1" sx={{ fontSize: "15px" }}>
              <strong>Phone:</strong> +21 071 154 1978 <br />
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4} display="flex" alignItems="center">
            <IconButton sx={{ color: primaryColor }}>
              <EmailIcon sx={{ margin: { xs: "15px", md: "17px" }, fontSize: { xs: "30px", md: "40px" } }} />
            </IconButton>
            <Typography variant="body1" sx={{ fontSize: "15px" }}>
              <strong>Email:</strong> amplenindustries@gmail.com <br />
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <UtilToastMessages
        isOpen={isSnackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        data="New Lead Created Successfully"
        severity="success"
      />
    </Box>
  );
};

export default ContactUs;